import { handleVersionMismatch } from 'actions/versionSync';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { purchaseWithBraintree } from 'actions/payment';
import { VersionMismatch } from 'services/BaseService';

import PaymentService from 'services/PaymentService';

import Loading from 'components/Loading';

import initializeTaxamo from './initializeTaxamo';
import updatePricing from './updatePricing';

import PaymentFailureMessageModal from 'components/Payment/PaymentFailureMessageModal';
import BraintreeViewComponent from './BraintreeViewComponent';

export class BraintreeViewContainer extends Component {
  // Are we mounted? (i.e., should we set state after async functions return)
  mounted = false;

  instance = null;

  // Braintree dropin instance
  service = new PaymentService(); // API request service

  state = {
    currency: undefined,
    isBreakdownVisible: false,
    isFailureModalOpen: false,
    isFetchingOnMount: true,
    isPurchasing: false,
    isSelectingCurrency: true,
    isWaitingForInstance: true,
    overrideNonce: undefined,
    reCaptchaResponse: undefined,
  };

  componentDidMount = async () => {
    const { dispatch } = this.props;
    this.mounted = true; // OK, now we're mounted
    this.setState({
      isFetchingOnMount: true,
      isSelectingCurrency: true,
    });
    const defaultCurrencyCode = '';

    // Fetch Braintree config, packages, etc.
    try {
      const { data } = await this.service.selectCurrency(defaultCurrencyCode);
      const {
        clientRequestToken,
        currencyCode,
        packages,
        taxamoConfig,
      } = data;

      // We may have unmounted while selectCurrency was happening; only call setState
      // if we're still mounted
      if (this.mounted) {
        // Put data into state
        this.setState({
          clientRequestToken,
          currencyCode,
          packages,
        });
      }

      // Initialize Taxamo
      initializeTaxamo(taxamoConfig.publicAuthToken);

      if (this.mounted) {
        // Now flag us as finished all this, so that we can render the component, and
        // when state has updated, call updatePricing()
        this.setState({ isFetchingOnMount: false, isSelectingCurrency: false }, updatePricing);
      }
    } catch (e) {
      if (e instanceof VersionMismatch) {
        dispatch(handleVersionMismatch(e));
        return;
      }
      throw e;
    }
  };

  componentWillUnmount = () => {
    // Flag that we're unmounted, so that async actions won't trigger calls to setState
    this.mounted = false;
  };

  onInstance = (instance) => {
    this.instance = instance;
    this.setState({ isWaitingForInstance: false });
  };

  handleReCaptchaChange = (recaptchaResponse) => {
    this.setState({ recaptchaResponse });
  };

  handleSelectCurrency = async (e) => {
    const currencyCode = e.target.value;
    this.setState({ isSelectingCurrency: true });
    // Fetch new Braintree config + packages
    const { data } = await this.service.selectCurrency(currencyCode);
    if (this.mounted) {
      // For reference, the key-values returned are enumerated in componentDidMount()
      this.setState({ ...data, isSelectingCurrency: false }, updatePricing);
    }
  };

  handleSelectPackage = (selectedPackage) => {
    this.setState({ selectedPackage });
  };

  handleSetOverrideNonce = (value) => {
    console.info(`setting override nonce to ${value}`);
    this.setState({ overrideNonce: value });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    const { dispatch, onSubmitFinished, onSubmitStarted } = this.props;
    const {
      currencyCode,
      recaptchaResponse,
      overrideNonce,
      selectedPackage: { quantity: nexAmount },
    } = this.state;

    // Clear error message and set spinners to go
    this.setState({
      isPurchasing: true,
      message: undefined,
    });

    // Get a payment method
    try {
      let nonce;
      if (overrideNonce && overrideNonce.length > 0) {
        console.info('overriding nonce');
        nonce = overrideNonce;
      } else {
        console.info('fetching nonce');
        nonce = (await this.instance.requestPaymentMethod()).nonce;
      }
      console.info(`sending nonce: ${nonce}`);
      const data = { currencyCode, nonce, nexAmount, recaptchaResponse };

      // Tell parent we've started submitting
      if (onSubmitStarted) {
        onSubmitStarted();
      }

      // Start the purchase
      await dispatch(purchaseWithBraintree(data));

      // Call the onSubmitFinished prop func that our parent passed us
      onSubmitFinished();
    } catch (error) {
      const { message } = error;
      if (message) {
        this.setState({
          isFailureModalOpen: true,
          message,
        });
      }
    }

    // Clean up — if we are still mounted
    if (this.mounted) {
      this.setState({ isPurchasing: false });
    }
  };

  handleTogglePriceBreakdown = () => {
    // Toggle whether VAT details are visible
    const { isBreakdownVisible } = this.state;
    this.setState({ isBreakdownVisible: !isBreakdownVisible });
  };

  render = () => {
    const {
      clientRequestToken,
      currency,
      currencyCode,
      isBreakdownVisible,
      isFailureModalOpen,
      isFetchingOnMount,
      isPurchasing,
      isSelectingCurrency,
      isWaitingForInstance,
      message,
      packages,
      selectedPackage,
    } = this.state;

    if (isFetchingOnMount) {
      return <Loading />;
    }

    if (!clientRequestToken) {
      return null;
    }

    return (
      <Fragment>
        <BraintreeViewComponent
          clientRequestToken={clientRequestToken}
          currency={currency}
          currencyCode={currencyCode}
          isBreakdownVisible={isBreakdownVisible}
          isPurchasing={isPurchasing}
          isSelectingCurrency={isSelectingCurrency}
          isWaitingForInstance={isWaitingForInstance}
          message={message}
          packages={packages}
          onInstance={this.onInstance}
          onReCaptchaChange={this.handleReCaptchaChange}
          onSelectCurrency={this.handleSelectCurrency}
          onSelectPackage={this.handleSelectPackage}
          onSetOverrideNonce={this.handleSetOverrideNonce}
          onSubmit={this.handleSubmit}
          onTogglePriceBreakdown={this.handleTogglePriceBreakdown}
          selectedPackage={selectedPackage}
        />
        <PaymentFailureMessageModal
          isOpen={isFailureModalOpen}
          message={message}
          onRequestClose={() => this.setState({ isFailureModalOpen: false })}
        />
      </Fragment>
    );
  }
}

BraintreeViewContainer.displayName = 'BraintreeViewContainer';

BraintreeViewContainer.propTypes = {
  onSubmitStarted: PropTypes.func.isRequired,
  onSubmitFinished: PropTypes.func.isRequired,
};

export default connect()(BraintreeViewContainer);